// src/contexts/AuthContext.tsx
import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { User } from "firebase/auth";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase/client";
import { buildInactivePlan, buildPlanPatch } from "../services/plan";
import { buildInitialUsageState, getPlanUsageLimits } from "../services/usageLimits";

type AuthContextValue = {
  user: User | null;
  loading: boolean;
  loginWithEmail: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  refreshUser: () => Promise<User | null>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

async function ensureUserDoc(user: User) {
  const ref = doc(db, "users", user.uid);
  const snap = await getDoc(ref);
  if (snap.exists()) return; // já existe, não sobrescrever

  const email = user.email?.trim() || "";
  const name = user.displayName?.trim() || (email ? email.split("@")[0] : "Usuario");
  const plan = buildInactivePlan();

  await setDoc(ref, {
    ...buildPlanPatch(plan),
    role: "free",
    tier: "free",
    planTier: "free",
    subscriptionStatus: "inactive",
    isPremium: false,
    displayName: name,
    name,
    email,
    username: email || user.uid,
    crm: "",
    rqe: "",
    specialty: "",
    gender: "",
    hospital: "",
    phone: "",
    professionalVerification: { status: "not_submitted", method: "none" },
    limits: getPlanUsageLimits("free"),
    usage: buildInitialUsageState(),
    profile: { name, email, crm: "", rqe: "", gender: "", crmState: "", specialty: "" },
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

function cloneFirebaseUser(user: User | null) {
  if (!user) return null;

  return Object.create(
    Object.getPrototypeOf(user),
    Object.getOwnPropertyDescriptors(user)
  ) as User;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(cloneFirebaseUser(u));
      setLoading(false);

      // Bootstrap: garantir que o documento Firestore existe para qualquer provider
      if (u) {
        void ensureUserDoc(u).catch((err) =>
          console.warn("[AuthContext] user doc bootstrap failed:", err?.message)
        );
      }
    });
    return () => unsub();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      loginWithEmail: async (email: string, password: string) => {
        await signInWithEmailAndPassword(auth, email, password);
      },
      loginWithGoogle: async () => {
        const provider = new GoogleAuthProvider();
        await signInWithPopup(auth, provider);
      },
      refreshUser: async () => {
        if (!auth.currentUser) {
          setUser(null);
          return null;
        }

        await auth.currentUser.reload();
        const nextUser = cloneFirebaseUser(auth.currentUser);
        setUser(nextUser);
        return nextUser;
      },
      logout: async () => {
        await signOut(auth);
      },
    }),
    [user, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
